import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Search, Eye, FileText, Calendar, DollarSign, AlertCircle, TrendingUp, CheckCircle, XCircle, CreditCard, X, Clock, ChevronDown, ChevronUp, Wallet, Trash2, Play, ShoppingCart, History, ExternalLink, FileUp, Download, Paperclip, Coins, Info, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import projectsService from '../services/projectsService';
import projectHoursService from '../services/projectHoursService';
import toast from 'react-hot-toast';
import ManualProjectModal from './ManualProjectModal';
import ProjectInitialBalances from './ProjectInitialBalances';
import PurchaseOrders from './PurchaseOrders';
import ProjectExtraIncomes from './ProjectExtraIncomes';
import ConfirmModal from './ConfirmModal';

// Cores por cargo
const ROLE_COLORS = {
  GERENCIA: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  RECEPCAO_COMPRAS_AGENDA: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  RECEPCAO_PROJETOS: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  PRODUCAO: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  MONTAGEM_CLIENTE: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' }
};

const ROLE_LABELS = {
  GERENCIA: 'Gerência',
  RECEPCAO_COMPRAS_AGENDA: 'Compras/Agenda',
  RECEPCAO_PROJETOS: 'Projetos',
  PRODUCAO: 'Produção',
  MONTAGEM_CLIENTE: 'Montagem'
};

const ProjectsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    client_name: '',
    priority: ''
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [paymentConditions, setPaymentConditions] = useState([]);
  const [selectedConditionId, setSelectedConditionId] = useState(null);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [savingCondition, setSavingCondition] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [projectHours, setProjectHours] = useState({});
  const [showInitialBalancesModal, setShowInitialBalancesModal] = useState(false);
  const [showPurchasesModal, setShowPurchasesModal] = useState(false);
  const [purchasesProject, setPurchasesProject] = useState(null);
  const [initialBalanceProject, setInitialBalanceProject] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [filesProject, setFilesProject] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showExtraIncomesModal, setShowExtraIncomesModal] = useState(false);
  const [extraIncomesProject, setExtraIncomesProject] = useState(null);
  const [expandedActions, setExpandedActions] = useState({});
  const [extraIncomesMap, setExtraIncomesMap] = useState({});
  const [showExtraIncomeInfoModal, setShowExtraIncomeInfoModal] = useState(false);
  const [infoModalProject, setInfoModalProject] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const [confirmDeleteFileId, setConfirmDeleteFileId] = useState(null);
  const [confirmApproveProjectId, setConfirmApproveProjectId] = useState(null);
  const [confirmRejectProjectId, setConfirmRejectProjectId] = useState(null);
  const [confirmRejectNotes, setConfirmRejectNotes] = useState('');
  const [showConfirmDeleteProjects, setShowConfirmDeleteProjects] = useState(false);

  const focusDebounceRef = useRef(null);
  const projectsRequestRef = useRef(0);
  const lastProjectQueryRef = useRef('');

  const loadProjects = useCallback(async (pageOverride = currentPage) => {
    const requestId = ++projectsRequestRef.current;
    try {
      setLoading(true);
      const response = await projectsService.getAllProjects({ ...filters, is_quick: 'false', limit: itemsPerPage, page: pageOverride });
      if (requestId !== projectsRequestRef.current) return;
      const projectsArray = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const pagination = response.data?.pagination || response.pagination || {};
      const total = pagination.total || projectsArray.length;
      const pages = pagination.pages || Math.ceil(total / itemsPerPage);
      setProjects(projectsArray);
      setTotalProjects(total);
      setTotalPages(pages);
      setHasInitialized(true);
    } catch (error) {
      console.error('❌ Erro ao carregar projetos:', error);
      toast.error('Erro ao carregar projetos: ' + (error.response?.data?.message || error.message));
    } finally {
      if (requestId === projectsRequestRef.current) {
        setLoading(false);
      }
    }
  }, [currentPage, itemsPerPage, filters]);

  // Recarregar quando filtros, página ou limite mudam, descartando respostas antigas.
  useEffect(() => {
    const queryKey = JSON.stringify({ filters, itemsPerPage });
    const filtersChanged = queryKey !== lastProjectQueryRef.current;
    const pageToLoad = filtersChanged ? 1 : currentPage;
    if (filtersChanged) {
      lastProjectQueryRef.current = queryKey;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    const timeout = setTimeout(() => loadProjects(pageToLoad), 350);
    return () => clearTimeout(timeout);
  }, [loadProjects, currentPage, itemsPerPage, filters]);

  // A seleção pertence à página visível e não deve atravessar consultas diferentes.
  useEffect(() => {
    setSelectedProjects([]);
  }, [currentPage, itemsPerPage, filters]);

  const loadStats = useCallback(async () => {
    try {
      const response = await projectsService.getProjectStats({ is_quick: 'false' });
      setStats(response.data || {});
    } catch (error) {
      console.error('❌ Erro ao carregar estatísticas:', error);
    }
  }, []);

  const loadExtraIncomesForProject = async (projectId) => {
    if (extraIncomesMap[projectId]) return;
    try {
      const response = await projectsService.getProjectExtraIncomes(projectId);
      const extras = response.data?.data || response.data || [];
      if (extras.length > 0) {
        setExtraIncomesMap(prev => ({ ...prev, [projectId]: extras }));
      }
    } catch (e) {
      console.error('Erro ao carregar extras:', e);
    }
  };

  // Recarregar projetos quando a página ganha foco (caso tenha sido criado em outra aba)
  useEffect(() => {
    const handleFocus = () => {
      if (focusDebounceRef.current) return;
      focusDebounceRef.current = setTimeout(() => {
        focusDebounceRef.current = null;
      }, 5000);
      loadProjects();
      loadStats();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      if (focusDebounceRef.current) {
        clearTimeout(focusDebounceRef.current);
      }
    };
  }, [loadProjects, loadStats]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const loadPaymentHistory = async (projectId) => {
    try {
      setLoadingHistory(true);
      const response = await projectsService.getPaymentConditionHistory(projectId);
      setPaymentHistory(response.data || []);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico de condições');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openFilesModal = async (project) => {
    setFilesProject(project);
    setShowFilesModal(true);
    await loadProjectFiles(project.id);
  };

  const loadProjectFiles = async (projectId) => {
    try {
      setLoadingFiles(true);
      const response = await projectsService.getProjectFiles(projectId);
      setProjectFiles(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
      toast.error('Erro ao carregar arquivos do projeto');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleUploadFiles = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingFiles(true);
      const formData = new FormData();
      formData.append('file_type', 'DOCUMENT');
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      await projectsService.uploadProjectFiles(filesProject.id, formData);
      toast.success(`${files.length} arquivo(s) enviado(s) com sucesso!`);
      await loadProjectFiles(filesProject.id);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error.response?.data?.message || 'Erro ao fazer upload de arquivos');
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await projectsService.downloadProjectFile(fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteFile = async () => {
    if (!confirmDeleteFileId) return;

    try {
      await projectsService.deleteProjectFile(confirmDeleteFileId);
      toast.success('Arquivo excluído com sucesso!');
      await loadProjectFiles(filesProject.id);
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error);
      toast.error(error.response?.data?.message || 'Erro ao deletar arquivo');
    } finally {
      setConfirmDeleteFileId(null);
    }
  };

  const toggleProjectDetails = async (projectId) => {
    const isExpanded = expandedProjects[projectId];

    if (isExpanded) {
      setExpandedProjects({ ...expandedProjects, [projectId]: false });
    } else {
      // Carregar horas do projeto
      try {
        const response = await projectHoursService.getProjectHours(projectId);

        // A API retorna { success: true, data: [...] }
        // Garantir que hoursData seja sempre um array
        let hoursData = response.data?.data || response.data || [];
        if (!Array.isArray(hoursData)) {
          hoursData = [];
        }

        setProjectHours(prev => ({
          ...prev,
          [projectId]: hoursData
        }));
        setExpandedProjects({ ...expandedProjects, [projectId]: true });
      } catch (error) {
        console.error('❌ Erro ao carregar horas do projeto:', error);
        toast.error('Erro ao carregar horas do projeto');
      }
    }
  };

  const handleApproveBudget = async () => {
    if (!confirmApproveProjectId) return;

    try {
      await projectsService.updateProjectStatus(confirmApproveProjectId, 'APPROVED');
      toast.success('Orçamento aprovado com sucesso!');
      loadProjects();
      loadStats();

      const project = projects.find(p => p.id === confirmApproveProjectId);
      if (project) {
        await openPaymentModal(project);
      }
    } catch (error) {
      console.error('Erro ao aprovar orçamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao aprovar orçamento');
    } finally {
      setConfirmApproveProjectId(null);
    }
  };

  const openPaymentModal = async (project) => {
    try {
      setSelectedProject(project);
      setLoadingConditions(true);

      // Primeiro tenta buscar as condições
      let response = await projectsService.getProjectPaymentConditions(project.id);

      // Se não tiver condições, sincroniza automaticamente
      if (!response.data || response.data.length === 0) {
        try {
          await projectsService.syncProjectPaymentConditions(project.id);
          // Busca novamente após sincronizar
          response = await projectsService.getProjectPaymentConditions(project.id);
        } catch (syncError) {
          // ignore sync error
        }
      }

      setPaymentConditions(response.data || []);
      setSelectedConditionId(response.selected_condition_id || null);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Erro ao carregar condições de pagamento:', error);
      toast.error('Erro ao carregar condições de pagamento');
    } finally {
      setLoadingConditions(false);
    }
  };

  const handleSelectPaymentCondition = async () => {
    if (!selectedProject) return;

    try {
      setSavingCondition(true);
      await projectsService.updateSelectedPaymentCondition(selectedProject.id, selectedConditionId);
      toast.success('Condição de pagamento escolhida salva com sucesso!');
      setShowPaymentModal(false);
      setSelectedProject(null);
      setSelectedConditionId(null);
      loadProjects();
    } catch (error) {
      console.error('Erro ao salvar condição de pagamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar condição de pagamento');
    } finally {
      setSavingCondition(false);
    }
  };

  const deleteSelectedProjects = async () => {
    try {
      setLoading(true);
      for (const projectId of selectedProjects) {
        await projectsService.deleteProject(projectId);
      }
      toast.success(`${selectedProjects.length} projeto(s) excluído(s) com sucesso!`);
      setSelectedProjects([]);
      loadProjects();
      loadStats();
    } catch (error) {
      console.error('Erro ao excluir projetos:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir projetos');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectBudget = async () => {
    if (!confirmRejectProjectId) return;

    try {
      await projectsService.updateProjectStatus(confirmRejectProjectId, 'REJECTED', confirmRejectNotes || '');
      toast.success('Orçamento rejeitado.');
      loadProjects();
      loadStats();
    } catch (error) {
      console.error('Erro ao rejeitar orçamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao rejeitar orçamento');
    } finally {
      setConfirmRejectProjectId(null);
      setConfirmRejectNotes('');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 md:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              📦 Projetos
            </h1>
            <p className="text-gray-600 text-sm md:text-base">
              Gerenciamento de projetos aprovados
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                loadProjects();
                loadStats();
              }}
              disabled={loading}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{loading ? 'Carregando...' : 'Carregar Projetos'}</span>
              <span className="sm:hidden">{loading ? '...' : 'Atualizar'}</span>
            </button>
            <button
              onClick={() => setShowManualModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Novo Projeto Manual</span>
            </button>
            {selectedProjects.length > 0 && (
              <button
                onClick={() => {
                  console.log('DEBUG: Botão Excluir clicado!', selectedProjects);
                  setShowConfirmDeleteProjects(true);
                }}
                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Excluir ({selectedProjects.length})</span>
                <span className="sm:hidden">({selectedProjects.length})</span>
              </button>
            )}
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
          <button
            onClick={() => setFilters({ ...filters, status: '' })}
            className={`bg-blue-50 p-4 rounded-lg border transition-all text-left ${filters.status === '' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-200 hover:border-blue-400'}`}
          >
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total_projects || 0}</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setFilters({ ...filters, status: filters.status === 'IN_PROGRESS' ? '' : 'IN_PROGRESS' })}
            className={`bg-yellow-50 p-4 rounded-lg border transition-all text-left ${filters.status === 'IN_PROGRESS' ? 'border-yellow-500 ring-2 ring-yellow-200' : 'border-yellow-200 hover:border-yellow-400'}`}
          >
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-600">Em Andamento</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.in_progress_count || 0}</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setFilters({ ...filters, status: filters.status === 'COMPLETED' ? '' : 'COMPLETED' })}
            className={`bg-green-50 p-4 rounded-lg border transition-all text-left ${filters.status === 'COMPLETED' ? 'border-green-500 ring-2 ring-green-200' : 'border-green-200 hover:border-green-400'}`}
          >
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Concluídos</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed_count || 0}</p>
              </div>
            </div>
          </button>

          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-sm text-gray-600">Alta Prioridade</p>
                <p className="text-2xl font-bold text-red-600">{stats.high_priority_count || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Todos os status</option>
              <option value="DRAFT">Rascunho</option>
              <option value="PLANNING">Planejamento</option>
              <option value="IN_PROGRESS">Em Andamento</option>
              <option value="PRODUCTION">Produção</option>
              <option value="ASSEMBLY">Montagem</option>
              <option value="COMPLETED">Concluído</option>
              <option value="DELIVERED">Entregue</option>
              <option value="PENDING">Pendente</option>
              <option value="PARTIAL">Parcial</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <input
              type="text"
              value={filters.client_name}
              onChange={(e) => setFilters({ ...filters, client_name: e.target.value })}
              placeholder="Buscar por nome..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Todas as prioridades</option>
              <option value="LOW">Baixa</option>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">Alta</option>
              <option value="URGENT">Urgente</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Projetos */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Projetos Recentes</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Carregando projetos...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">Nenhum projeto encontrado</p>
            <p className="text-sm">Os projetos aparecerão aqui quando forem aprovados</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Ações
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    <input
                      type="checkbox"
                      checked={projects.length > 0 && projects.every(project => selectedProjects.includes(project.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProjects(projects.map(p => p.id));
                        } else {
                          setSelectedProjects([]);
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Código
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36 sm:w-44">
                    Cliente
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projeto
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Status
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Prioridade
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    Horas
                  </th>
                  {user.role === 'GERENCIA' && (
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Orçamento
                    </th>
                  )}
                  {user.role === 'GERENCIA' && (
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Valor Projeto
                    </th>
                  )}
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Criado em
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map((project) => {
                  // Garantir que hours seja sempre um array
                  const hoursRaw = projectHours[project.id];
                  const hours = Array.isArray(hoursRaw) ? hoursRaw : [];
                  const isExpanded = expandedProjects[project.id];
                  const isActionsExpanded = expandedActions[project.id];

                  return (
                    <React.Fragment key={project.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-2 py-2 whitespace-nowrap text-left text-xs font-medium">
                          {isActionsExpanded ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setExpandedActions({ ...expandedActions, [project.id]: false })}
                                className="text-gray-400 hover:text-red-600 p-0.5"
                                title="Ocultar ações"
                              >
                                <ChevronUp className="w-3 h-3" />
                              </button>
                              <div className="flex flex-row flex-wrap gap-1">
                              {project.status === 'AWAITING_CLIENT' && (
                                <>
                                  <button
                                    onClick={() => setConfirmApproveProjectId(project.id)}
                                    className="text-green-600 hover:text-green-900 p-0.5 rounded"
                                    title="Aprovar orçamento"
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const notes = window.prompt('Informe o motivo da rejeição (opcional):');
                                      if (notes === null) return;
                                      setConfirmRejectProjectId(project.id);
                                      setConfirmRejectNotes(notes);
                                    }}
                                    className="text-red-600 hover:text-red-900 p-0.5 rounded"
                                    title="Rejeitar orçamento"
                                  >
                                    <XCircle className="w-3 h-3" />
                                  </button>
                                </>
                              )}

                              <button
                                onClick={() => navigate(`/projetos/${project.id}/orcamento`)}
                                className="text-green-600 hover:text-green-900 p-0.5 rounded"
                                title="Abrir orçamento"
                              >
                                <DollarSign className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setInitialBalanceProject(project);
                                  setShowInitialBalancesModal(true);
                                }}
                                className="text-emerald-600 hover:text-emerald-900 p-0.5 rounded"
                                title="Contrato & Condições Financeiras"
                              >
                                <Wallet className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => navigate(`/projetos/${project.id}/horas`)}
                                className="text-blue-600 hover:text-blue-900 p-0.5 rounded"
                                title="Editar horas do projeto"
                              >
                                <Clock className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setPurchasesProject(project);
                                  setShowPurchasesModal(true);
                                }}
                                className="text-amber-600 hover:text-amber-900 p-0.5 rounded"
                                title="Compras do Projeto"
                              >
                                <ShoppingCart className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => navigate(`/projetos/${project.id}/execucao`)}
                                className="text-purple-600 hover:text-purple-900 p-0.5 rounded"
                                title="Execução do Projeto"
                              >
                                <Play className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => openFilesModal(project)}
                                className="text-indigo-600 hover:text-indigo-900 p-0.5 rounded"
                                title="Documentos do Projeto"
                              >
                                <Paperclip className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setExtraIncomesProject(project);
                                  setShowExtraIncomesModal(true);
                                }}
                                className="text-yellow-600 hover:text-yellow-900 p-0.5 rounded"
                                title="Recebimentos Extras"
                              >
                                <Coins className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  const clientDisplay = project.client_full_name
                                    ? `${project.client_full_name}${project.client_apelido && project.client_apelido.toLowerCase() !== project.client_full_name.toLowerCase() ? ` (${project.client_apelido})` : ''}`
                                    : project.client_name;
                                  const info = [
                                    `Código: ${project.project_code || `PROJ-${project.id}`}`,
                                    `Cliente: ${clientDisplay}`,
                                    project.project_description && `\nDescrição:\n${project.project_description}`,
                                    project.survey_3d_observations && `\nObservações do Projeto 3D:\n${project.survey_3d_observations}`,
                                    project.survey_code && `\nLevantamento: ${project.survey_code}`
                                  ].filter(Boolean).join('\n');
                                  toast.success(info, {
                                    duration: 6000,
                                    style: { whiteSpace: 'pre-line' }
                                  });
                                }}
                                className="text-blue-600 hover:text-blue-900 p-0.5 rounded"
                                title="Visualizar detalhes e observações"
                              >
                                <Eye className="w-3 h-3" />
                              </button>
                            </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setExpandedActions({ ...expandedActions, [project.id]: true })}
                              className="text-gray-400 hover:text-blue-600 p-0.5"
                              title="Mostrar ações"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                        <td className="px-1 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={selectedProjects.includes(project.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProjects([...selectedProjects, project.id]);
                                } else {
                                  setSelectedProjects(selectedProjects.filter(id => id !== project.id));
                                }
                              }}
                              className="w-3 h-3 text-blue-600 rounded"
                            />
                            <button
                              onClick={() => toggleProjectDetails(project.id)}
                              className="p-0.5 hover:bg-gray-100 rounded"
                              title="Ver detalhes de horas"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-900">
                            {project.project_code || `PROJ-${project.id}`}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          {(() => {
                            const fullName = project.client_full_name || project.client_name || '-';
                            const apelido = project.client_apelido?.trim();
                            const isAutoSlug = apelido && /_\d+$/.test(apelido) && (
                              apelido.toLowerCase().replace(/_\d+$/, '').replace(/_/g, ' ') === fullName.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 30).trim()
                            );
                            const hasValidApelido = apelido && !isAutoSlug && apelido.toLowerCase() !== fullName.toLowerCase();

                            return (
                              <>
                                <div
                                  className="text-xs font-medium text-gray-900 truncate"
                                  title={hasValidApelido ? `${fullName} (Apelido: ${apelido})` : fullName}
                                >
                                  <span>{fullName}</span>
                                  {hasValidApelido && (
                                    <span className="text-gray-500 font-normal"> / {apelido}</span>
                                  )}
                                </div>
                                {hasValidApelido && (
                                  <div className="text-[10px] text-blue-600 font-medium truncate flex items-center gap-0.5 mt-0.5" title={`Apelido: ${apelido}`}>
                                    <span>👤 {apelido}</span>
                                  </div>
                                )}
                                {project.survey_code && (
                                  <div className="text-[10px] text-gray-500 mt-0.5">
                                    Lev: {project.survey_code}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-xs font-medium text-gray-900 truncate" title={project.project_name}>
                            {project.project_name}
                          </div>
                          {(project.project_description || project.survey_3d_observations) && (
                            <div className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">
                              {project.project_description || project.survey_3d_observations}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${projectsService.getStatusColor(project.status)}`}>
                            {projectsService.formatStatus(project.status)}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${projectsService.getPriorityColor(project.priority)}`}>
                            {projectsService.formatPriority(project.priority)}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {hours.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {hours.slice(0, 2).map((h) => {
                                const colors = ROLE_COLORS[h.role] || ROLE_COLORS.PRODUCAO;
                                return (
                                  <span
                                    key={h.role}
                                    className={`inline-flex px-1.5 py-0.5 text-[10px] rounded-full ${colors.bg} ${colors.text}`}
                                    title={`${ROLE_LABELS[h.role] || h.role}: ${h.estimated_hours}h`}
                                  >
                                    {ROLE_LABELS[h.role]?.substring(0, 3) || h.role?.substring(0, 3)}: {h.estimated_hours}h
                                  </span>
                                );
                              })}
                              {hours.length > 2 && (
                                <span className="text-[10px] text-gray-500">+{hours.length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              -
                            </span>
                          )}
                        </td>
                        {user.role === 'GERENCIA' && (
                          <td className="px-2 py-2 whitespace-nowrap">
                            <div className="text-xs text-gray-900">
                              {project.estimated_budget ? projectsService.formatCurrency(project.estimated_budget) : '-'}
                            </div>
                          </td>
                        )}
                        {user.role === 'GERENCIA' && (
                          <td className="px-2 py-2 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                  <button
                                    onClick={async () => {
                                      await loadExtraIncomesForProject(project.id);
                                      setInfoModalProject(project);
                                      setShowExtraIncomeInfoModal(true);
                                    }}
                                    className="text-xs font-medium text-gray-900 hover:text-blue-600 flex items-center gap-1"
                                    title="Ver composição do valor do projeto (Contrato + Extras)"
                                  >
                                    {projectsService.formatCurrency(parseFloat(project.project_value) || 0)}
                                    <Info className="w-3 h-3" />
                                  </button>
                              </div>
                            </div>
                          </td>
                        )}
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500">
                          {projectsService.formatDate(project.created_at)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${project.id}-details`}>
                          <td colSpan={user.role === 'GERENCIA' ? "10" : "9"} className="px-4 py-4 bg-gray-50 border-b">
                            <div className="ml-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                Planejamento de Horas por Cargo
                              </h4>
                              {hours.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">Nenhuma hora planejada para este projeto</p>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {hours.map((h, idx) => {
                                    const colors = ROLE_COLORS[h.role] || ROLE_COLORS.PRODUCAO;
                                    const statusColors = {
                                      PENDING: 'bg-gray-100 text-gray-600',
                                      SCHEDULED: 'bg-yellow-100 text-yellow-700',
                                      IN_PROGRESS: 'bg-blue-100 text-blue-700',
                                      COMPLETED: 'bg-green-100 text-green-700'
                                    };
                                    return (
                                      <div
                                        key={idx}
                                        className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                                            {ROLE_LABELS[h.role] || h.role}
                                          </span>
                                          <span className={`text-xs px-2 py-0.5 rounded ${statusColors[h.status] || 'bg-gray-100 text-gray-600'}`}>
                                            {h.status === 'PENDING' ? 'Pendente' : h.status === 'SCHEDULED' ? 'Agendado' : h.status === 'IN_PROGRESS' ? 'Em Andamento' : h.status === 'COMPLETED' ? 'Concluído' : h.status}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                          <div>
                                            <span className="text-gray-500">Horas:</span>
                                            <span className="font-semibold text-gray-900 ml-1">{h.estimated_hours}h</span>
                                          </div>
                                          {h.scheduled_hours_per_day && (
                                            <div>
                                              <span className="text-gray-500">Por dia:</span>
                                              <span className="font-semibold text-gray-900 ml-1">{h.scheduled_hours_per_day}h</span>
                                            </div>
                                          )}
                                          {h.assigned_to_name && (
                                            <div className="col-span-2">
                                              <span className="text-gray-500">Responsável:</span>
                                              <span className="font-semibold text-gray-900 ml-1">{h.assigned_to_name}</span>
                                            </div>
                                          )}
                                          {h.scheduled_start_date && (
                                            <div>
                                              <span className="text-gray-500">De:</span>
                                              <span className="font-semibold text-gray-900 ml-1">
                                                {new Date(h.scheduled_start_date).toLocaleDateString('pt-BR')}
                                              </span>
                                            </div>
                                          )}
                                          {h.scheduled_end_date && (
                                            <div>
                                              <span className="text-gray-500">Até:</span>
                                              <span className="font-semibold text-gray-900 ml-1">
                                                {new Date(h.scheduled_end_date).toLocaleDateString('pt-BR')}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                        {h.notes && (
                                          <div className="mt-2 pt-2 border-t border-gray-200">
                                            <span className="text-xs text-gray-500">Obs:</span>
                                            <p className="text-xs text-gray-700 mt-1">{h.notes}</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Paginação */}
            {projects.length > 0 && (
              <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-700">
                    Itens {totalProjects > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} a {Math.min(currentPage * itemsPerPage, totalProjects)} de {totalProjects}
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-2 py-1 border border-gray-300 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 min-w-[60px]"
                  >
                    <span className="hidden sm:inline">Anterior</span>
                    <span className="sm:hidden">&lt;</span>
                  </button>

                  <span className="text-xs text-gray-700 px-2">
                    {currentPage}/{totalPages} (de {totalProjects})
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 border border-gray-300 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 min-w-[60px]"
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <span className="sm:hidden">&gt;</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Seleção de Condição de Pagamento */}
      {showPaymentModal && selectedProject && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Selecionar Condição de Pagamento Escolhida pelo Cliente
              </h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedProject(null);
                  setSelectedConditionId(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Projeto:</strong> {selectedProject.project_code} - {selectedProject.client_name}
              </p>
              <p className="text-sm text-gray-600">
                Selecione qual condição de pagamento o cliente escolheu:
              </p>
            </div>

            {loadingConditions ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Carregando condições de pagamento...</p>
              </div>
            ) : paymentConditions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Nenhuma condição de pagamento disponível para este projeto.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                {paymentConditions.map((condition) => {
                  const conditionName = condition.condition_name || condition.custom_name || 'Condição Personalizada';
                  const conditionDescription = condition.condition_description || condition.custom_description || '';
                  const isSelected = selectedConditionId === condition.id;

                  return (
                    <div
                      key={condition.id}
                      onClick={() => setSelectedConditionId(condition.id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                          }`}>
                          {isSelected && (
                            <div className="w-3 h-3 rounded-full bg-white"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{conditionName}</div>
                          {conditionDescription && (
                            <div className="text-sm text-gray-600 mt-1">{conditionDescription}</div>
                          )}
                          {(condition.installments > 1 || condition.custom_installments > 1) && (
                            <div className="text-xs text-gray-500 mt-1">
                              {condition.installments || condition.custom_installments}x
                              {condition.has_interest || condition.custom_has_interest
                                ? ` (Com juros)`
                                : ` (Sem juros)`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedProject(null);
                  setSelectedConditionId(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => loadPaymentHistory(selectedProject.id)}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <History className="w-4 h-4" />
                Ver Histórico
              </button>
              <button
                onClick={handleSelectPaymentCondition}
                disabled={!selectedConditionId || savingCondition}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingCondition ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Histórico de Condições de Pagamento */}
      {showHistoryModal && selectedProject && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Histórico de Condições de Pagamento
              </h3>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedProject(null);
                  setPaymentHistory([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                <strong>Projeto:</strong> {selectedProject.project_code} - {selectedProject.client_name}
              </p>
            </div>

            {loadingHistory ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Carregando histórico...</p>
              </div>
            ) : paymentHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Nenhum registro de mudança de condição de pagamento.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentHistory.map((record, index) => {
                  const snapshot = record.condition_snapshot || {};
                  const previousCondition = snapshot.previous;
                  const newCondition = snapshot.new;

                  return (
                    <div key={record.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === 0 ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>
                            <Clock className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {index === 0 ? 'Condição atual' : 'Mudança anterior'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(record.changed_at).toLocaleString('pt-BR')}
                            </p>
                            {record.changed_by_name && (
                              <p className="text-sm text-gray-500">
                                Por: {record.changed_by_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-4">
                        {previousCondition && (
                          <div className="text-sm">
                            <span className="text-gray-500">De: </span>
                            <span className="font-medium text-red-600">
                              {previousCondition.name || 'Nenhuma'}
                            </span>
                            {previousCondition.installments > 1 && (
                              <span className="text-gray-500"> ({previousCondition.installments}x)</span>
                            )}
                          </div>
                        )}
                        {newCondition && (
                          <div className="text-sm">
                            <span className="text-gray-500">Para: </span>
                            <span className="font-medium text-green-600">
                              {newCondition.name || 'Nenhuma'}
                            </span>
                            {newCondition.installments > 1 && (
                              <span className="text-gray-500"> ({newCondition.installments}x)</span>
                            )}
                          </div>
                        )}
                      </div>

                      {record.reason && (
                        <p className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">Motivo: </span>
                          {record.reason}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t mt-4">
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedProject(null);
                  setPaymentHistory([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Saldos Iniciais */}
      {showInitialBalancesModal && initialBalanceProject && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-4/5 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Contrato & Condições Financeiras — {initialBalanceProject.project_code}
              </h3>
              <button
                onClick={() => {
                  setShowInitialBalancesModal(false);
                  setInitialBalanceProject(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Cliente:</strong> {initialBalanceProject.client_name}
              </p>
              <p className="text-sm text-gray-500">
                Defina os valores de contrato, entrada e parcelamento do projeto. As entradas e parcelas serão geradas automaticamente no módulo financeiro.
              </p>
            </div>

            <ProjectInitialBalances projectId={initialBalanceProject.id} estimatedBudget={initialBalanceProject.estimated_budget} projectName={initialBalanceProject.project_name} clientName={initialBalanceProject.client_name} />
          </div>
        </div>
      )}

      {/* Modal de Pedidos de Compra */}
      {showPurchasesModal && purchasesProject && (
        <PurchaseOrders
          projectId={purchasesProject.id}
          projectName={purchasesProject.project_name || purchasesProject.client_name}
          onClose={() => {
            setShowPurchasesModal(false);
            setPurchasesProject(null);
          }}
        />
      )}

      {/* Modal de Arquivos do Projeto */}
      {showFilesModal && filesProject && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Arquivos do Projeto - {filesProject.project_code}
              </h3>
              <button
                onClick={() => {
                  setShowFilesModal(false);
                  setFilesProject(null);
                  setProjectFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                <strong>Cliente:</strong> {filesProject.client_name} | <strong>Projeto:</strong> {filesProject.project_name}
              </p>
            </div>

            {/* Upload Area */}
            <div className="mb-6 p-6 border-2 border-dashed border-gray-300 rounded-lg text-center">
              <FileUp className="w-10 h-10 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600 mb-3">
                Arraste arquivos aqui ou clique para selecionar
              </p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                <FileUp className="w-4 h-4" />
                Selecionar Arquivos
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onChange={handleUploadFiles}
                  disabled={uploadingFiles}
                  className="hidden"
                />
              </label>
              {uploadingFiles && (
                <p className="mt-2 text-sm text-blue-600">Enviando arquivos...</p>
              )}
            </div>

            {/* File List */}
            {loadingFiles ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Carregando arquivos...</p>
              </div>
            ) : projectFiles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Paperclip className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Nenhum arquivo enviado para este projeto.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">{projectFiles.length} arquivo(s)</h4>
                {projectFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <FileText className="w-8 h-8 text-blue-500 flex-shrink-0" />
                      <div className="overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate" title={file.original_name}>
                          {file.original_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.file_size / 1024).toFixed(1)} KB
                          {file.uploaded_by_name && ` | Enviado por ${file.uploaded_by_name}`}
                          {file.created_at && ` | ${new Date(file.created_at).toLocaleDateString('pt-BR')}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleDownloadFile(file.id, file.original_name)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                        title="Baixar arquivo"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteFileId(file.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded"
                        title="Excluir arquivo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t mt-4">
              <button
                onClick={() => {
                  setShowFilesModal(false);
                  setFilesProject(null);
                  setProjectFiles([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Projeto Manual */}
      {showManualModal && (
        <ManualProjectModal
          onClose={() => setShowManualModal(false)}
          onRefresh={() => {
            loadProjects();
            loadStats();
          }}
        />
      )}

      {/* Modal de Recebimentos Extras */}
      {showExtraIncomesModal && extraIncomesProject && (
        <ProjectExtraIncomes
          project={extraIncomesProject}
          onClose={() => {
            setShowExtraIncomesModal(false);
            setExtraIncomesProject(null);
            setExtraIncomesMap({});
            loadProjects();
          }}
          onExtraIncomesChanged={() => {
            queryClient.invalidateQueries({ queryKey: ['cashbox-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['cashbox-stats'] });
          }}
        />
      )}

      {/* Modal de Info - Detalhamento do Valor Projeto */}
      {showExtraIncomeInfoModal && infoModalProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Detalhamento do Valor Projeto
              </h3>
              <button
                onClick={() => {
                  setShowExtraIncomeInfoModal(false);
                  setInfoModalProject(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">Projeto</p>
                <p className="font-medium text-gray-900">{infoModalProject.project_code} - {infoModalProject.client_name}</p>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Valor Base do Projeto</span>
                  <span className="font-medium text-gray-900">
                    {projectsService.formatCurrency(parseFloat(infoModalProject.project_value) || 0)}
                  </span>
                </div>
                {(() => {
                  const extras = extraIncomesMap[infoModalProject.id] || [];
                  const extrasTotal = extras.reduce((sum, e) => sum + (parseFloat(e.total_amount) || 0), 0);
                  const baseValue = parseFloat(infoModalProject.project_value) || 0;
                  return (
                    <>
                      {extras.length > 0 && (
                        <>
                          <div className="border-t pt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Recebimentos Extras ({extras.length})</p>
                            {extras.map((extra) => (
                              <div key={extra.id} className="flex justify-between items-center p-2 bg-green-50 rounded-lg mb-2">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{extra.description}</p>
                                  <p className="text-xs text-gray-500">{extra.installments}x - {extra.status}</p>
                                </div>
                                <span className="font-medium text-green-700">
                                  {projectsService.formatCurrency(parseFloat(extra.total_amount))}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg">
                            <span className="text-sm font-medium text-gray-900">Total Extras</span>
                            <span className="font-bold text-green-700">
                              {projectsService.formatCurrency(extrasTotal)}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between items-center p-3 bg-blue-100 rounded-lg border-2 border-blue-300">
                        <span className="text-base font-bold text-gray-900">Valor Total do Projeto</span>
                        <span className="font-bold text-blue-700 text-lg">
                          {projectsService.formatCurrency(baseValue + extrasTotal)}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setShowExtraIncomeInfoModal(false);
                  setInfoModalProject(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteFileId && (
        <ConfirmModal
          show={!!confirmDeleteFileId}
          isOpen={!!confirmDeleteFileId}
          title="Excluir Arquivo"
          message="Tem certeza que deseja excluir este arquivo?"
          onConfirm={() => {
            handleDeleteFile();
          }}
          onCancel={() => setConfirmDeleteFileId(null)}
        />
      )}

      {confirmApproveProjectId && (
        <ConfirmModal
          show={!!confirmApproveProjectId}
          isOpen={!!confirmApproveProjectId}
          title="Aprovar Orçamento"
          message="Tem certeza que deseja aprovar este orçamento? O projeto será movido para planejamento."
          onConfirm={() => {
            handleApproveBudget();
          }}
          onCancel={() => setConfirmApproveProjectId(null)}
        />
      )}

      {confirmRejectProjectId && (
        <ConfirmModal
          show={!!confirmRejectProjectId}
          isOpen={!!confirmRejectProjectId}
          title="Rejeitar Orçamento"
          message="Tem certeza que deseja rejeitar este orçamento? O projeto será cancelado."
          onConfirm={() => {
            handleRejectBudget();
          }}
          onCancel={() => {
            setConfirmRejectProjectId(null);
            setConfirmRejectNotes('');
          }}
        />
      )}

      {showConfirmDeleteProjects && (
        <ConfirmModal
          show={showConfirmDeleteProjects}
          title="Excluir Projetos"
          message={`Tem certeza que deseja excluir ${selectedProjects.length} projeto(s)? Esta ação não pode ser desfeita.`}
          onConfirm={() => {
            setShowConfirmDeleteProjects(false);
            deleteSelectedProjects();
          }}
          onCancel={() => setShowConfirmDeleteProjects(false)}
        />
      )}

    </div>
  );
};

export default ProjectsList;
